import React, { useState } from 'react';
import { User, Plus, X } from 'lucide-react';
import { ReferenteInlineForm, EMPTY_REFERENTE_DRAFT } from '../ReferenteInlineForm';

interface ReferentiRowsProps {
  formData: Record<string, any>;
  updateFormData: (updates: Record<string, any>) => void;
}

/**
 * Referenti del cliente in stile Notion: lista a righe (click per modificare,
 * x per rimuovere) + form inline a righe di proprietà dietro la riga ghost.
 */
export function ReferentiRows({ formData, updateFormData }: ReferentiRowsProps) {
  const contacts: any[] = formData.contacts || [];
  // null = chiuso; -1 = nuovo; >=0 = modifica del contatto a quell'indice
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<string, any>>(EMPTY_REFERENTE_DRAFT);

  const openNew = () => { setDraft({ ...EMPTY_REFERENTE_DRAFT }); setEditingIdx(-1); };
  const openEdit = (idx: number) => { setDraft({ ...EMPTY_REFERENTE_DRAFT, ...contacts[idx] }); setEditingIdx(idx); };
  const set = (k: string) => (v: any) => setDraft((p) => ({ ...p, [k]: v }));

  const save = () => {
    if (!draft.first_name.trim() && !draft.last_name.trim() && !draft.email.trim()) {
      setEditingIdx(null);
      return;
    }
    const next = [...contacts];
    if (editingIdx === -1) next.push(draft);
    else if (editingIdx !== null) next[editingIdx] = draft;
    updateFormData({ contacts: next });
    setEditingIdx(null);
  };

  const remove = (idx: number) => {
    updateFormData({ contacts: contacts.filter((_, i) => i !== idx) });
    if (editingIdx === idx) setEditingIdx(null);
  };

  return (
    <div>
      {contacts.map((c, idx) => (
        <div
          key={idx}
          className="group flex min-h-[30px] cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06]"
          onClick={() => openEdit(idx)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') openEdit(idx); }}
        >
          <User className="h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
          <span className="min-w-0 flex-1 truncate text-[13px] text-[#37352F] dark:text-white">
            {`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Referente'}
            {c.role && <span className="text-[rgba(55,53,47,0.5)] dark:text-gray-400"> · {c.role}</span>}
            {c.email && <span className="text-[rgba(55,53,47,0.5)] dark:text-gray-400"> · {c.email}</span>}
          </span>
          <button
            type="button"
            aria-label="Rimuovi referente"
            onClick={(e) => { e.stopPropagation(); remove(idx); }}
            className="opacity-0 hover:text-red-500 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
          >
            <X className="h-3.5 w-3.5 text-[#787774]" strokeWidth={1.5} />
          </button>
        </div>
      ))}

      {editingIdx === null ? (
        <button
          type="button"
          onClick={openNew}
          className="mt-0.5 flex items-center gap-1.5 rounded px-1.5 py-1 -mx-0 text-[13px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          Aggiungi referente
        </button>
      ) : (
        <ReferenteInlineForm
          title={editingIdx === -1 ? 'Nuovo referente' : 'Modifica referente'}
          draft={draft}
          onField={set}
          onCancel={() => setEditingIdx(null)}
          onSave={save}
          saveLabel={editingIdx === -1 ? 'Aggiungi' : 'Salva'}
        />
      )}
    </div>
  );
}
